import React from 'react';
import { useMatchStore } from '@/data/matchStore';
import { useRingStore } from '@/data/ringStore';
import { useI18n } from '@/i18n/I18nProvider';
import { Link } from 'react-router-dom';
import { Shield, HeartHandshake, Flame, ChevronRight, Swords, Map, MapPin, Trophy, HelpCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Button } from '@/components/ui/button';
import { getSectionContent } from '@/data/cmsStore';
import { useState, useEffect } from 'react';

const HeroMiniCards = () => {
  const { isMatchEnabled } = useMatchStore();
  const { isRingEnabled } = useRingStore();
  const { isRtl, lang } = useI18n();
  const [cms, setCms] = useState(() => getSectionContent("minicards", "home"));

  useEffect(() => {
    const handleCmsChange = () => setCms(getSectionContent("minicards", "home"));
    window.addEventListener("cmsLayoutChanged", handleCmsChange);
    window.addEventListener("cmsLayoutDraftChanged", handleCmsChange);
    window.addEventListener("stagingModeChanged", handleCmsChange);
    return () => {
      window.removeEventListener("cmsLayoutChanged", handleCmsChange);
      window.removeEventListener("cmsLayoutDraftChanged", handleCmsChange);
      window.removeEventListener("stagingModeChanged", handleCmsChange);
    };
  }, []);

  const djellabaLink = cms?.minicard_djellaba_link || "";

  const renderDjellabaCardContent = () => (
    <div className="h-full w-full rounded-2xl bg-gradient-to-br from-emerald-500/95 to-teal-700/95 backdrop-blur-sm p-4 flex flex-col justify-between relative overflow-hidden">
      {/* subtle background map icon */}
      <Map className="absolute -bottom-4 -right-4 w-24 h-24 text-white/10 transform rotate-12 group-hover:scale-110 transition-transform" />
      
      <div className="flex items-start justify-between relative z-10 mb-2 gap-1">
        <div className="flex flex-col items-center gap-1 -mt-1">
          <span className="flex h-4 items-center px-1.5 rounded-full bg-emerald-900/50 text-[8px] font-bold text-emerald-100 uppercase border border-emerald-400/30 animate-pulse whitespace-nowrap shadow-sm">
            {lang === 'ar' ? "قريباً" : lang === 'zgh' ? "ⴷⵖⵢⴰ" : "Bientôt"}
          </span>
          <div className="bg-white/20 p-2 rounded-full backdrop-blur-md shrink-0">
            <MapPin className="w-5 h-5 text-emerald-100" />
          </div>
        </div>
      </div>
    
      <div className="relative z-10 mt-auto">
        <h3 className="text-white font-display font-black text-sm sm:text-base leading-tight mb-1">
          {cms?.[`minicard_djellaba_title_${lang}`] || (lang === 'ar' ? "كاينين بالجلباب المحلي!" : lang === 'zgh' ? "ⴽⴰⵢⵏⵉⵏ ⵙ ⵜⵊⵍⵍⴰⴱⵜ ⵜⴰⴷⵖⵔⴰⵏⵜ!" : "Kayn'in en Djellaba locale !")}
        </h3>
        <p className="text-emerald-100 text-[10px] sm:text-xs font-semibold flex items-center gap-1 group-hover:text-white transition-colors">
          {cms?.[`minicard_djellaba_subtitle_${lang}`] || (lang === 'ar' ? "من أجل مجالات ترابية للفرص والمشاريع." : lang === 'zgh' ? "ⵉ ⵉⴽⴰⵍⵉⵡⵏ ⵏ ⵜⵎⵍⴷⵉⵡⵉⵏ ⴷ ⵉⵙⵏⴼⴰⵕⵏ." : "Pour des territoires d'opportunités et de projets.")}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-row lg:flex-col gap-3 w-full">
        {/* Match Ton Parti Mini Card */}
        {isMatchEnabled && (
          <Link 
            to={cms?.minicard_match_link || "/match"} 
            className="flex-1 group relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 via-pink-600 to-purple-600 p-[2px] transition-transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-rose-500/20"
          >
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="h-full w-full rounded-2xl bg-gradient-to-br from-rose-500/95 to-purple-600/95 backdrop-blur-sm p-4 flex flex-col justify-between relative overflow-hidden">
              {/* Tinder-like subtle background flame/heart */}
              <HeartHandshake className="absolute -bottom-4 -right-4 w-24 h-24 text-white/10 transform -rotate-12 group-hover:scale-110 transition-transform" />
              
              <div className="flex items-start justify-between relative z-10 mb-2">
                <div className="bg-white/20 p-2 rounded-full backdrop-blur-md">
                  <Flame className="w-5 h-5 text-yellow-300 animate-pulse" />
                </div>
                <span className="flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
              </div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-white font-display font-black text-sm sm:text-base leading-tight mb-1">
                  {cms?.[`minicard_match_title_${lang}`] || (lang === 'ar' ? "ماتش مع حزبك" : lang === 'zgh' ? "ⵎⴰⵜⵛ ⴷ ⵓⴽⴰⴱⴰⵕ ⵏⵏⴽ" : "Match ton Parti")}
                </h3>
                <p className="text-rose-100 text-[10px] sm:text-xs font-semibold flex items-center gap-1 group-hover:text-white transition-colors">
                  {cms?.[`minicard_match_subtitle_${lang}`] || (lang === 'ar' ? "سوايب دابا" : lang === 'zgh' ? "ⵙⵡⴰⵢⴱ ⴰⴷ ⵜⵙⵙⵏⵜ" : "Swipe pour découvrir")}
                  <ChevronRight className={`w-3 h-3 ${isRtl ? 'rotate-180' : ''}`} />
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Le Ring Mini Card */}
        {isRingEnabled && (
          <Link 
            to={cms?.minicard_ring_link || "/ring"} 
            className="flex-1 group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-900 via-red-800 to-black p-[2px] transition-transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-red-900/40"
          >
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 mix-blend-overlay" />
            <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            
            <div className="h-full w-full rounded-2xl bg-black/40 backdrop-blur-sm p-4 flex flex-col justify-between relative overflow-hidden border border-red-500/30">
              {/* Arena-like subtle background swords */}
              <Swords className="absolute -bottom-4 -right-4 w-24 h-24 text-red-500/10 transform rotate-12 group-hover:scale-110 group-hover:text-red-500/20 transition-all" />
              
              <div className="flex items-start justify-between relative z-10 mb-2">
                <div className="bg-red-500/20 p-2 rounded-full backdrop-blur-md border border-red-500/50">
                  <Shield className="w-5 h-5 text-red-500" />
                </div>
                <span className="flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
              </div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-white font-display font-black text-sm sm:text-base leading-tight mb-1 uppercase tracking-wider">
                  {cms?.[`minicard_ring_title_${lang}`] || (lang === 'ar' ? "حلبة النقاش" : lang === 'zgh' ? "ⴰⵏⵏⴰⵔ ⵏ ⵓⵎⵙⴰⵡⴰⵍ" : "Le Ring")}
                </h3>
                <p className="text-red-200 text-[10px] sm:text-xs font-semibold flex items-center gap-1 group-hover:text-white transition-colors">
                  {cms?.[`minicard_ring_subtitle_${lang}`] || (lang === 'ar' ? "ادخل المواجهة" : lang === 'zgh' ? "ⴽⵛⵎ ⵙ ⵓⵏⵏⴰⵔ" : "Entrer dans l'arène")}
                  <ChevronRight className={`w-3 h-3 ${isRtl ? 'rotate-180' : ''}`} />
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Quiz Politique Mini Card */}
        <Link 
          to={cms?.minicard_quiz_link || "/quiz"} 
          className="flex-1 group relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 p-[2px] transition-transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-orange-500/20"
        >
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="h-full w-full rounded-2xl bg-gradient-to-br from-amber-500/95 to-orange-600/95 backdrop-blur-sm p-4 flex flex-col justify-between relative overflow-hidden">
            <Trophy className="absolute -bottom-4 -right-4 w-24 h-24 text-white/10 transform rotate-12 group-hover:scale-110 transition-transform" />
            
            <div className="flex items-start justify-between relative z-10 mb-2">
              <div className="bg-white/20 p-2 rounded-full backdrop-blur-md">
                <HelpCircle className="w-5 h-5 text-amber-100" />
              </div>
              <span className="flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-amber-200 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-100"></span>
              </span>
            </div>
            
            <div className="relative z-10 mt-auto">
              <h3 className="text-white font-display font-black text-sm sm:text-base leading-tight mb-1">
                {cms?.[`minicard_quiz_title_${lang}`] || (lang === 'ar' ? "اختبار سياسي" : lang === 'zgh' ? "ⴰⵙⵉⵜⴳ ⴰⵙⵔⵜⴰⵏ" : "Quiz Politique")}
              </h3>
              <p className="text-amber-100 text-[10px] sm:text-xs font-semibold flex items-center gap-1 group-hover:text-white transition-colors">
                {cms?.[`minicard_quiz_subtitle_${lang}`] || (lang === 'ar' ? "اكتشف توجهك" : lang === 'zgh' ? "ⴰⴼ ⴰⵎⵓⵙⵙⵓ ⵏⵏⴽ" : "Découvrez votre profil")}
                <ChevronRight className={`w-3 h-3 ${isRtl ? 'rotate-180' : ''}`} />
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* Kayn'in Local Teaser Mini Card */}
      {djellabaLink ? (
        <Link
          to={djellabaLink}
          className="flex-1 lg:flex-none group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-600 p-[2px] cursor-pointer transition-transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-emerald-500/20"
        >
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          {renderDjellabaCardContent()}
        </Link>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <div 
              className="flex-1 lg:flex-none group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-600 p-[2px] cursor-pointer transition-transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-emerald-500/20"
            >
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              {renderDjellabaCardContent()}
            </div>
          </PopoverTrigger>
          <PopoverContent 
            side="bottom" 
            align="center" 
            className="w-[320px] sm:w-[350px] p-5 bg-card/95 backdrop-blur-md border border-border shadow-2xl z-50 rounded-2xl"
          >
            <div className="flex flex-col gap-4">
              <div className="text-sm md:text-base font-body leading-relaxed text-foreground">
                {cms?.[`minicard_djellaba_message_${lang}`] ? (
                  <div dangerouslySetInnerHTML={{ __html: cms[`minicard_djellaba_message_${lang}`] }} />
                ) : (
                  lang === 'ar' ? (
                    <>قريباً: اكتشاف جماعتك الترابية، المساهمة في التنمية الترابية، المشاركة في الحكامة المحلية والتفاعل مع الفاعلين المحليين!<br/><br/><span className="font-bold text-primary">المنصة تنخرط في اللامركزية...!</span></>
                  ) : lang === 'zgh' ? (
                    <>ⴷⵖⵢⴰ: ⴰⴼ ⵜⴰⴳⵔⴰⵡⵜ ⵜⴰⴷⵖⵔⴰⵏⵜ ⵏⵏⴽ, ⵜⵜⴽⴽⵉ ⴳ ⵜⵏⴱⴰⴹⵜ ⵜⴰⴷⵖⵔⴰⵏⵜ ⴷ ⵙⴰⵡⵍ ⴷ ⵉⵎⵙⵜⴰⵢⵏ ⵏⵏⴽ!<br/><br/><span className="font-bold text-primary">ⵜⴰⵙⵏⵙⵉⵜ ⴰⵔ ⵜⵜⴷⴷⵓ ⵙ ⵜⵍⴰⵎⴰⵎⵎⴰⵙⵜ...!</span></>
                  ) : (
                    <>Bientôt disponible : Découvrir votre collectivité territoriale, contribuer au développement territorial, participer à la gouvernance locale et interagir avec les acteurs locaux.<br/><br/><span className="font-bold text-primary">La plateforme se décentralise ...!</span></>
                  )
                )}
              </div>
              <div className="flex justify-end mt-2">
                <PopoverPrimitive.Close asChild>
                  <Button size="sm" className="font-semibold px-6 rounded-full">
                    {lang === 'ar' ? 'حسناً' : 'OK'}
                  </Button>
                </PopoverPrimitive.Close>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default HeroMiniCards;
